/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility Edit one AI assistant persona: identity, temperament, speech, rules and set replies.
 * @state-owner local draft state; dataset store owns the saved persona
 * @allowed-imports React; shared components and hooks; module model and schema
 * @forbidden direct API client; navigation; student-only surfaces
 * @public-api assistants settings page
 * @side-effects none; saving is delegated to the caller
 * @tests modules/assistants/model/assistant.test.ts and the workspace Playwright flows
 */

import { type FormEvent, useEffect, useState } from 'react';
import { SectionLabel } from '../../../shared/components/Modal';
import type { AssistantRecord, AssistantReplyRule } from '../../../shared/lib/model';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  ACCENTS,
  ANSWER_POLICY_LABEL,
  EMOJI_USE_LABEL,
  PERSONA_LIMIT,
  TONE_HINT,
  TONE_LABEL,
  TRAITS,
  traitWord,
} from '../model/assistant';
import { assistantSchema } from '../schemas/assistant';

const AVATARS = ['🦉', '📐', '🐧', '🤖', '🦊', '🐳', '🌱', '🧭', '🎈', '🍀'];
const HINT_STEPS = [0, 1, 2, 3, 4];

export function AssistantEditor({
  assistant,
  formId,
  onActionStateChange,
  onDelete,
  onSave,
  savePlacement = 'inline',
}: {
  assistant: AssistantRecord;
  formId?: string;
  onActionStateChange?: (state: { dirty: boolean; saving: boolean }) => void;
  onDelete?: () => void | Promise<void>;
  onSave: (assistant: AssistantRecord) => void | Promise<void>;
  savePlacement?: 'external' | 'inline';
}) {
  const t = useTranslator();
  const [draft, setDraft] = useState(assistant);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(assistant);
  /**
   * The products that do this well ask for a handful of things up front — avatar, name, tagline,
   * the opening line — and keep the rest one click away. Everything below the fold still matters,
   * but a teacher writing their first persona should not have to scroll past dials to save one.
   */
  const [advanced, setAdvanced] = useState(false);
  const patch = (next: Partial<AssistantRecord>) =>
    setDraft((current) => ({ ...current, ...next }));

  const patchRule = (id: string, next: Partial<AssistantReplyRule>) =>
    patch({
      replyRules: draft.replyRules.map((rule) => (rule.id === id ? { ...rule, ...next } : rule)),
    });

  useEffect(() => {
    onActionStateChange?.({ dirty, saving });
  }, [dirty, onActionStateChange, saving]);

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    const parsed = assistantSchema.safeParse(draft);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('還有欄位沒填完'));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        ...(parsed.data as unknown as AssistantRecord),
        persona: parsed.data.persona ?? '',
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('儲存失敗，請稍後再試'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form aria-busy={saving} className="asedit" id={formId} onSubmit={save}>
      {/* What the student actually sees: the avatar, who they are, and the first thing said. */}
      <div className="aspv" style={{ ['--accent' as string]: draft.accent }}>
        <span className="aspva">{draft.emoji}</span>
        <div className="aspvt">
          <b>{draft.name || t('還沒命名')}</b>
          <span>{draft.tagline || t('一句話介紹會出現在派發時的選單')}</span>
        </div>
        <p className="aspvb">{draft.greeting || t('開場白會是學生看到的第一句話')}</p>
      </div>

      <SectionLabel first>{t('身分')}</SectionLabel>
      <div className="asrow">
        <label className="asfld">
          <span>{t('名字')}</span>
          <input onChange={(event) => patch({ name: event.target.value })} value={draft.name} />
        </label>
        <label className="asfld">
          <span>{t('一句話介紹')}</span>
          <input
            onChange={(event) => patch({ tagline: event.target.value })}
            value={draft.tagline}
          />
        </label>
      </div>
      <div className="asava">
        {AVATARS.map((emoji) => (
          <button
            className={`asav ${draft.emoji === emoji ? 'on' : ''}`}
            key={emoji}
            style={{ ['--accent' as string]: draft.accent }}
            type="button"
            onClick={() => patch({ emoji })}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="asdots">
        {ACCENTS.map((accent) => (
          <button
            aria-label={accent}
            className={`asdot ${draft.accent === accent ? 'on' : ''}`}
            key={accent}
            style={{ background: accent }}
            type="button"
            onClick={() => patch({ accent })}
          />
        ))}
      </div>
      <label className="asfld">
        <span>{t('開場白')}</span>
        <textarea
          onChange={(event) => patch({ greeting: event.target.value })}
          placeholder={t('學生點開助教看到的第一句話，也最能決定他之後的語氣')}
          rows={2}
          value={draft.greeting}
        />
      </label>
      <label className="asfld">
        <span className="asflh">
          {t('角色設定')}
          <em className={draft.persona.length > PERSONA_LIMIT ? 'over' : ''}>
            {`${draft.persona.length} / ${PERSONA_LIMIT}`}
          </em>
        </span>
        <textarea
          onChange={(event) => patch({ persona: event.target.value })}
          placeholder={t('他是誰、怎麼看待學生、遇到卡住的人會先做什麼')}
          rows={4}
          value={draft.persona}
        />
      </label>

      <SectionLabel>{t('個性')}</SectionLabel>
      <div className="fbar" style={{ margin: '0 0 10px' }}>
        {(Object.keys(TONE_LABEL) as (keyof typeof TONE_LABEL)[]).map((tone) => (
          <button
            className={`ch ${draft.tone === tone ? 'on' : ''}`}
            key={tone}
            title={t(TONE_HINT[tone])}
            type="button"
            onClick={() => patch({ tone })}
          >
            {t(TONE_LABEL[tone])}
          </button>
        ))}
      </div>
      {TRAITS.map((trait) => (
        <div className="astrait" key={trait.key}>
          <span className="astl">{t(trait.label)}</span>
          <input
            aria-label={t(trait.label)}
            max={100}
            min={0}
            onChange={(event) => patch({ [trait.key]: Number(event.target.value) })}
            step={5}
            type="range"
            value={draft[trait.key]}
          />
          <em>{t(traitWord(draft[trait.key], trait))}</em>
        </div>
      ))}

      <button className="asmore" type="button" onClick={() => setAdvanced(!advanced)}>
        {advanced ? t('收合進階設定') : t('進階設定：說話方式、教學規則、特定回覆')}
      </button>
      {advanced ? (
        <>
          <SectionLabel>{t('說話方式')}</SectionLabel>
          <div className="asrow">
            <label className="asfld">
              <span>{t('怎麼稱呼學生')}</span>
              <input
                onChange={(event) => patch({ address: event.target.value })}
                value={draft.address}
              />
            </label>
            <label className="asfld">
              <span>{t('口頭禪')}</span>
              <input
                onChange={(event) => patch({ catchphrase: event.target.value })}
                value={draft.catchphrase}
              />
            </label>
          </div>
          <div className="asrow">
            <div className="asfld">
              <span>{t('表情符號')}</span>
              <div className="fbar" style={{ margin: 0 }}>
                {(Object.keys(EMOJI_USE_LABEL) as (keyof typeof EMOJI_USE_LABEL)[]).map((use) => (
                  <button
                    className={`ch ${draft.emojiUse === use ? 'on' : ''}`}
                    key={use}
                    type="button"
                    onClick={() => patch({ emojiUse: use })}
                  >
                    {t(EMOJI_USE_LABEL[use])}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <SectionLabel>{t('教學規則')}</SectionLabel>
          <div className="asrow">
            <div className="asfld">
              <span>{t('提示幾次後交給老師')}</span>
              <div className="fbar" style={{ margin: 0 }}>
                {HINT_STEPS.map((step) => (
                  <button
                    className={`ch ${draft.hintsBeforeTeacher === step ? 'on' : ''}`}
                    key={step}
                    type="button"
                    onClick={() => patch({ hintsBeforeTeacher: step })}
                  >
                    {step}
                  </button>
                ))}
              </div>
            </div>
            <div className="asfld">
              <span>{t('答案')}</span>
              <div className="fbar" style={{ margin: 0 }}>
                {(Object.keys(ANSWER_POLICY_LABEL) as (keyof typeof ANSWER_POLICY_LABEL)[]).map(
                  (policy) => (
                    <button
                      className={`ch ${draft.answerPolicy === policy ? 'on' : ''}`}
                      key={policy}
                      type="button"
                      onClick={() => patch({ answerPolicy: policy })}
                    >
                      {t(ANSWER_POLICY_LABEL[policy])}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
          <label className="asfld">
            <span>{t('絕對不要做的事')}</span>
            <textarea
              onChange={(event) => patch({ forbidden: event.target.value })}
              placeholder={t('例如：不要直接寫出答案、不要說「很簡單」')}
              rows={2}
              value={draft.forbidden}
            />
          </label>

          <SectionLabel>{t('特定回覆')}</SectionLabel>
          <p className="asnote">{t('學生說出左邊那句話時，助教一定用右邊這句回。')}</p>
          {draft.replyRules.map((rule) => (
            <div className="asrule" key={rule.id}>
              <input
                aria-label={t('學生說')}
                onChange={(event) => patchRule(rule.id, { trigger: event.target.value })}
                placeholder={t('學生說')}
                value={rule.trigger}
              />
              <input
                aria-label={t('助教回')}
                onChange={(event) => patchRule(rule.id, { reply: event.target.value })}
                placeholder={t('助教回')}
                value={rule.reply}
              />
              <button
                className="ib2"
                title={t('刪掉')}
                type="button"
                onClick={() =>
                  patch({ replyRules: draft.replyRules.filter((item) => item.id !== rule.id) })
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            className="bt sm"
            type="button"
            onClick={() =>
              patch({
                replyRules: [
                  ...draft.replyRules,
                  { id: `r${draft.replyRules.length + 1}-${draft.id}`, trigger: '', reply: '' },
                ],
              })
            }
          >
            {t('加一條')}
          </button>
        </>
      ) : null}
      <div className="asfoot">
        <label className="asdef">
          <input
            checked={draft.isDefault}
            disabled={assistant.isDefault}
            onChange={(event) => patch({ isDefault: event.target.checked })}
            type="checkbox"
          />
          {t('設為預設助教')}
        </label>
        {error ? <span className="asmsg">{error}</span> : null}
        <div className="sp" />
        {onDelete && !assistant.isDefault ? (
          <button className="bt danger" disabled={saving} type="button" onClick={onDelete}>
            {t('刪掉')}
          </button>
        ) : null}
        {savePlacement === 'inline' ? (
          <button className="bt pri" disabled={!dirty || saving} type="submit">
            {saving ? t('儲存中…') : t('儲存')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
